import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [activeCategory, setActiveCategory] = useState('inspection');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: 'Inspektion & Wartung',
      description: 'Regelmäßige Wartung nach Herstellervorgaben',
      items: ['Ölwechsel', 'Bremsenwartung', 'Klimaservice', 'TÜV-Vorbereitung'],
      icon: 'ri-settings-3-line',
      bgColor: 'bg-gradient-to-br from-orange-500 to-orange-600'
    },
    {
      title: 'Diagnose & Elektronik',
      description: 'Modernste Diagnosetechnik für alle Fahrzeugmarken',
      items: ['Fehlerdiagnose', 'Motorelektronik', 'Steuergeräte', 'Software-Updates'],
      icon: 'ri-cpu-line',
      bgColor: 'bg-gradient-to-br from-gray-800 to-gray-900'
    },
    {
      title: 'Reparatur aller Marken',
      description: 'Professionelle Reparaturen für alle Fahrzeugtypen',
      items: ['Motorinstandsetzung', 'Getriebe', 'Fahrwerk', 'Auspuff'],
      icon: 'ri-tools-line',
      bgColor: 'bg-gradient-to-br from-orange-500 to-orange-600'
    }
  ];

  const priceCategories = [
    {
      id: 'inspection',
      name: 'Inspektion & Wartung',
      icon: 'ri-settings-3-line',
      items: [
        { service: 'Inspektion klein', description: 'Öl- & Sichtprüfung nach Herstellervorgaben', price: '120' },
        { service: 'Inspektion groß', description: 'Umfassende Inspektion inkl. Filter', price: '170' },
        { service: 'Ölwechsel', description: 'inkl. Ölfilter (Öl extra)', price: '100' },
        { service: 'Bremsenservice', description: 'Bremsbeläge vorne/hinten', price: '220' },
        { service: 'Batterie wechseln', description: 'inkl. Funktionsprüfung', price: '50' },
        { service: 'Klimaservice', description: 'Desinfektion & Dichtigkeitsprüfung', price: '130' }
      ]
    },
    {
      id: 'diagnosis',
      name: 'Diagnose & Elektronik',
      icon: 'ri-cpu-line',
      items: [
        { service: 'Fehlerdiagnose', description: 'OBD-Auslesung', price: '20' }
      ]
    },
    {
      id: 'tires',
      name: 'Reifen & Räder',
      icon: 'ri-steering-2-line',
      items: [
        { service: 'Reifenmontage', description: '', price: '50' },
        { service: 'Reifenwechsel komplett', description: '', price: '60' },
        { service: 'Auswuchten (pro Rad)', description: '', price: '130' },
        { service: 'Einlagerung pro Saison', description: '', price: '45' }
      ]
    },
    {
      id: 'tuv',
      name: 'HU & AU',
      icon: 'ri-shield-check-line',
      items: [
        { service: 'HU inkl. AU', description: '', price: '250' }
      ]
    },
    {
      id: 'emergency',
      name: 'Notdienst',
      icon: 'ri-alarm-warning-line',
      items: [
        { service: 'Abschleppen innerorts', description: '', price: '150' },
        { service: 'Starthilfe', description: '', price: '15' },
        { service: 'Pannenhilfe', description: '', price: '60' }
      ]
    }
  ];

  const testimonials = [
    {
      name: 'Michael Schmidt',
      rating: 5,
      text: 'Sehr professionelle Werkstatt! Die Diagnose war präzise und die Reparatur wurde schnell und sauber durchgeführt. Faire Preise und kompetente Beratung.',
      avatar: 'MS'
    },
    {
      name: 'Sandra Weber',
      rating: 5,
      text: 'Endlich eine Werkstatt, der man vertrauen kann. Alles wird transparent erklärt und die Qualität der Arbeit ist hervorragend.',
      avatar: 'SW'
    },
    {
      name: 'Thomas Müller',
      rating: 5,
      text: 'Kompetentes Team, moderne Ausstattung und faire Preise. Ich bringe mein Auto nur noch hierher!',
      avatar: 'TM'
    },
    {
      name: 'Julia Hoffmann',
      rating: 5,
      text: 'Sehr zufrieden mit dem Service. Die Mitarbeiter nehmen sich Zeit für Erklärungen und arbeiten sehr gewissenhaft.',
      avatar: 'JH'
    },
    {
      name: 'Andreas Klein',
      rating: 5,
      text: 'Top Werkstatt! Schnelle Terminvergabe, professionelle Arbeit und freundlicher Service. Absolut empfehlenswert!',
      avatar: 'AK'
    },
    {
      name: 'Maria Fischer',
      rating: 5,
      text: 'Beste Werkstatt in der Region. Ehrliche Beratung ohne unnötige Reparaturen. Komme gerne wieder!',
      avatar: 'MF'
    }
  ];

  const features = [
    { icon: 'ri-shield-check-line', text: 'Meisterbetrieb mit Zertifizierung' },
    { icon: 'ri-time-line', text: 'Über 30 Jahre Erfahrung' },
    { icon: 'ri-team-line', text: 'Qualifiziertes Fachpersonal' },
    { icon: 'ri-price-tag-3-line', text: 'Transparente Preisgestaltung' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
                CAR SYSTEM
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#startseite" className="text-gray-700 hover:text-orange-500 font-medium transition-colors whitespace-nowrap cursor-pointer">Startseite</a>
              <a href="#dienstleistungen" className="text-gray-700 hover:text-orange-500 font-medium transition-colors whitespace-nowrap cursor-pointer">Dienstleistungen</a>
              <a href="#preise" className="text-gray-700 hover:text-orange-500 font-medium transition-colors whitespace-nowrap cursor-pointer">Preise</a>
              <a href="#ueber-uns" className="text-gray-700 hover:text-orange-500 font-medium transition-colors whitespace-nowrap cursor-pointer">Über uns</a>
              <a href="#galerie" className="text-gray-700 hover:text-orange-500 font-medium transition-colors whitespace-nowrap cursor-pointer">Galerie</a>
              <a href="#kontakt" className="text-gray-700 hover:text-orange-500 font-medium transition-colors whitespace-nowrap cursor-pointer">Kontakt</a>
              <a href="tel:+4915222619198" className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg hover:scale-105 transition-all font-medium whitespace-nowrap cursor-pointer">Jetzt anrufen</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="startseite" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/692833cc1c86b4b0ba26b92be2ff3127.jpg" 
            alt="CAR SYSTEM Audi" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-4xl">
            <div className="mb-8 animate-fade-in">
              <span className="text-orange-500 font-bold text-xl uppercase tracking-widest">Willkommen bei</span>
            </div>
            <h1 className="text-7xl md:text-8xl font-black text-white mb-6 leading-tight animate-slide-up">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">CAR SYSTEM</span>
            </h1>
            <p className="text-3xl text-white/95 mb-4 font-bold">
              PROFESSIONELLE AUTOREPARATUR
            </p>
            <p className="text-xl text-white/80 mb-10 font-light">
              Zuverlässig. Transparent. Kompetent.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:+4915222619198" className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-5 rounded-xl hover:shadow-2xl hover:scale-105 transition-all font-bold text-lg whitespace-nowrap cursor-pointer inline-block">
                <i className="ri-phone-line mr-2"></i>
                Jetzt anrufen
              </a>
              <a href="#preise" className="text-white border-2 border-white px-10 py-5 rounded-xl hover:bg-white hover:text-gray-900 transition-all font-bold text-lg whitespace-nowrap cursor-pointer inline-block backdrop-blur-sm">
                Preise ansehen
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="dienstleistungen" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-500 font-bold text-sm uppercase tracking-widest">CAR SYSTEM Leistungen</span>
            <h2 className="text-6xl font-black text-gray-900 mt-4 mb-6">Unsere Dienstleistungen</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Von der regelmäßigen Wartung bis zur komplexen Reparatur – CAR SYSTEM ist Ihr zuverlässiger Partner für alle Fahrzeugmarken
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`${service.bgColor} text-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer transform`}
                onMouseEnter={() => setActiveService(index)}
              >
                <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm mb-6">
                  <i className={`${service.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="mb-6 text-white/90">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-xl mr-3 text-white"></i>
                      <span className="text-white/95">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prices Section */}
      <section id="preise" className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-500 font-bold text-sm uppercase tracking-widest">CAR SYSTEM Preisliste</span>
            <h2 className="text-6xl font-black text-gray-900 mt-4 mb-6">Transparente Preise</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Faire und transparente Preisgestaltung für alle Leistungen bei CAR SYSTEM – keine versteckten Kosten
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {priceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-bold transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>

          {/* Price Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {priceCategories
              .find((cat) => cat.id === activeCategory)
              ?.items.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-transparent hover:border-orange-500"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{item.service}</h4>
                      {item.description && (
                        <p className="text-sm text-gray-600">{item.description}</p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
                        {item.price}€
                      </div>
                    </div>
                  </div>
                  <a
                    href="tel:+4915222619198"
                    className="block w-full text-center bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 rounded-lg hover:shadow-lg hover:scale-105 transition-all font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Jetzt anfragen
                  </a>
                </div>
              ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-lg mb-6">
              Alle Preise verstehen sich zzgl. Material und MwSt. | Individuelle Angebote auf Anfrage
            </p>
            <a
              href="tel:+4915222619198"
              className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-4 rounded-xl hover:shadow-2xl hover:scale-105 transition-all font-bold text-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Kostenlose Beratung: 0152 226 191 98
            </a>
          </div>
        </div>
      </section>

      {/* Brand Banner */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            CAR SYSTEM
          </h2>
          <p className="text-2xl text-white/95 font-light">
            Ihr Partner für professionelle Fahrzeugpflege in Geisenfeld
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="text-orange-500 font-bold text-sm uppercase tracking-widest">CAR SYSTEM Kundenmeinungen</span>
            <h2 className="text-6xl font-black text-gray-900 mt-4">Was unsere Kunden sagen</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">Google Bewertung</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-orange-500 text-lg"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="ueber-uns" className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-full min-h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20automotive%20service%20team%20in%20modern%20workshop%20experienced%20mechanics%20wearing%20clean%20branded%20uniforms%20standing%20confidently%20in%20pristine%20garage%20environment%20state%20of%20the%20art%20equipment%20visible%20bright%20professional%20lighting%20team%20portrait%20contemporary%20workshop%20design%20luxury%20service%20center%20atmosphere%20welcoming%20professional%20staff&width=800&height=1000&seq=team-professional-002&orientation=portrait" 
                alt="CAR SYSTEM Team" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-8">
                <p className="text-white font-bold text-2xl">CAR SYSTEM Team</p>
              </div>
            </div>
            <div>
              <span className="text-orange-500 font-bold text-sm uppercase tracking-widest">Über CAR SYSTEM</span>
              <h2 className="text-6xl font-black text-gray-900 mt-4 mb-8 leading-tight">
                30 Jahre Erfahrung in der Fahrzeugreparatur
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Seit über drei Jahrzehnten ist <strong>CAR SYSTEM</strong> Ihr verlässlicher Partner für professionelle Fahrzeugreparaturen und Wartungen. Unser Meisterbetrieb steht für höchste Qualität, Transparenz und Kundenorientierung.
                </p>
                <p>
                  Mit modernster Diagnosetechnik und einem erfahrenen Team aus qualifizierten Kfz-Mechatronikern betreut <strong>CAR SYSTEM</strong> alle Fahrzeugmarken. Dabei legen wir besonderen Wert auf ehrliche Beratung und faire Preisgestaltung.
                </p>
                <p>
                  Unser Anspruch bei <strong>CAR SYSTEM</strong> ist es, Ihr Fahrzeug nicht nur zu reparieren, sondern Ihnen ein sicheres und zuverlässiges Fahrerlebnis zu garantieren. Vertrauen Sie auf unsere Expertise und Erfahrung.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-12">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start bg-gray-50 p-4 rounded-xl hover:bg-orange-50 transition-colors">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 mr-4 flex-shrink-0">
                      <i className={`${feature.icon} text-xl text-white`}></i>
                    </div>
                    <span className="text-gray-700 font-semibold">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galerie" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-500 font-bold text-sm uppercase tracking-widest">CAR SYSTEM Galerie</span>
            <h2 className="text-6xl font-black text-gray-900 mt-4">Unsere Werkstatt</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 h-96 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=premium%20automotive%20workshop%20interior%20with%20luxury%20car%20on%20professional%20hydraulic%20lift%20ultra%20modern%20diagnostic%20equipment%20pristine%20clean%20organized%20workspace%20bright%20LED%20lighting%20contemporary%20industrial%20design%20glass%20partitions%20high%20end%20repair%20facility%20professional%20service%20bay%20state%20of%20the%20art%20technology%20minimalist%20aesthetic&width=1200&height=800&seq=gallery-premium-main-002&orientation=landscape" 
                alt="CAR SYSTEM Werkstatt" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="h-96 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=advanced%20automotive%20diagnostic%20computer%20system%20displaying%20detailed%20vehicle%20data%20modern%20professional%20workshop%20technology%20mechanic%20using%20high%20tech%20diagnostic%20equipment%20multiple%20screens%20showing%20engine%20analysis%20clean%20contemporary%20workspace%20premium%20service%20tools&width=600&height=800&seq=gallery-diagnostic-tech-002&orientation=portrait" 
                alt="CAR SYSTEM Diagnose" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="h-96 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=skilled%20automotive%20technician%20performing%20precision%20engine%20repair%20work%20detailed%20mechanical%20service%20professional%20mechanic%20using%20specialized%20tools%20clean%20modern%20workshop%20environment%20focused%20craftsmanship%20quality%20automotive%20service%20premium%20repair%20work%20contemporary%20garage%20setting&width=600&height=800&seq=gallery-precision-work-002&orientation=portrait" 
                alt="CAR SYSTEM Reparatur" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="md:col-span-2 h-96 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20automotive%20service%20center%20exterior%20view%20contemporary%20commercial%20building%20design%20clean%20professional%20facade%20customer%20parking%20area%20welcoming%20entrance%20large%20glass%20windows%20premium%20business%20location%20well%20maintained%20property%20professional%20signage%20bright%20daylight&width=1200&height=800&seq=gallery-modern-exterior-002&orientation=landscape" 
                alt="CAR SYSTEM Außenansicht" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-500 font-bold text-sm uppercase tracking-widest">CAR SYSTEM Kontakt</span>
            <h2 className="text-6xl font-black text-gray-900 mt-4 mb-6">Wir sind für Sie da</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kontaktieren Sie CAR SYSTEM telefonisch oder per E-Mail. Wir freuen uns auf Ihre Anfrage!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <div className="text-center group">
              <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-phone-line text-4xl text-white"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-xl">Telefon</h4>
              <a href="tel:+4915222619198" className="text-2xl text-gray-700 hover:text-orange-500 transition-colors cursor-pointer font-bold">
                0152 226 191 98
              </a>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-mail-line text-4xl text-white"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-xl">E-Mail</h4>
              <a href="mailto:Carsystem461@gmail.com" className="text-xl text-gray-700 hover:text-orange-500 transition-colors cursor-pointer break-all">
                Carsystem461@gmail.com
              </a>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-map-pin-line text-4xl text-white"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-xl">Adresse</h4>
              <p className="text-gray-700 text-lg">Im Gewerbegebiet Geisenfeld</p>
            </div>
          </div>

          <div className="mt-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-12 max-w-4xl mx-auto shadow-lg">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-gray-900 mb-4 text-xl">Öffnungszeiten</h4>
                <p className="text-gray-700 text-lg mb-2">Mo - Fr: 08:00 - 18:00 Uhr</p>
                <p className="text-gray-700 text-lg">Sa: 10:00 - 18:00 Uhr</p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4 text-xl">Geschäftsführer</h4>
                <p className="text-gray-700 text-lg">Andre Hofer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div>
              <div className="text-3xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                CAR SYSTEM
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Ihr zuverlässiger Partner für professionelle Fahrzeugreparaturen und Wartungen seit über 30 Jahren.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-6">Schnellzugriff</h4>
              <ul className="space-y-3">
                <li><a href="#dienstleistungen" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Leistungen</a></li>
                <li><a href="#preise" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Preise</a></li>
                <li><a href="#ueber-uns" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Über uns</a></li>
                <li><a href="#galerie" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Galerie</a></li>
                <li><a href="#kontakt" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Kontakt</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Karriere</a></li>
                <li><a href="#" className="text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Impressum</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-6">Kontakt</h4>
              <ul className="space-y-3 mb-8">
                <li className="text-gray-400">Tel: 0152 226 191 98</li>
                <li className="text-gray-400">E-Mail: Carsystem461@gmail.com</li>
                <li className="text-gray-400">Im Gewerbegebiet Geisenfeld</li>
              </ul>
              <h4 className="text-xl font-bold mb-4">Geschäftsführer</h4>
              <p className="text-gray-400 mb-6">Andre Hofer</p>
              <h4 className="text-xl font-bold mb-4 text-orange-500">Notdienst</h4>
              <a href="tel:+4915222619198" className="text-2xl font-bold text-orange-500 hover:text-orange-400 transition-colors cursor-pointer">
                0152 226 191 98
              </a>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 CAR SYSTEM. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors text-sm cursor-pointer">Datenschutz</a>
              <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors text-sm cursor-pointer">AGB</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 transition-colors text-sm cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
